import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  ArrowLeft, MoreVertical, Phone, Video, User, Users,
  Send, Mic, MicOff, Paperclip, Image as ImageIcon, X, 
  Check, CheckCheck, Play, Pause, File, Download,
  Trash2, Reply, Copy, Archive, Clock, AlertCircle, StopCircle,
  Plus, ArrowUp, FileText, Square
} from 'lucide-react';
import { messagesAPI, uploadAPI, conversationsAPI } from '../../services/messagingAPI';
import { API_URL } from '../../config/api';
import CallModal from './CallModal';

const ChatWindow = ({ 
  conversation, 
  onBack, 
  onConversationUpdate,
  onTyping,
  onMarkRead,
  typingUsers,
  isWebSocketConnected 
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  
  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  
  // Reply
  const [replyTo, setReplyTo] = useState(null);
  
  // Menu contextuel
  const [contextMenu, setContextMenu] = useState(null);
  
  // Menu attachments style WhatsApp
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  
  // Appels vocaux/vidéo
  const [showCallModal, setShowCallModal] = useState(false);
  const [callType, setCallType] = useState('audio'); // 'audio' | 'video'
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const currentUserId = localStorage.getItem('ecosystem_user_id');

  const loadMessages = useCallback(async (beforeId = null) => {
    if (!conversation?.id) return;
    
    // Messages de démo pour les conversations de test
    const getDemoMessages = () => {
      const now = new Date();
      if (conversation.id === 'demo-1') {
        return [
          {
            id: 'msg-1',
            content: 'Bonjour ! Je suis intéressée par votre annonce pour le canapé.',
            type: 'text',
            sender_id: 'user-1',
            is_mine: false,
            created_at: new Date(now - 3600000 * 2).toISOString(),
            read_by: []
          },
          {
            id: 'msg-2',
            content: 'Bonjour Marie ! Oui il est toujours disponible. Il est en très bon état.',
            type: 'text',
            sender_id: currentUserId,
            is_mine: true,
            created_at: new Date(now - 3600000 * 1.5).toISOString(),
            read_by: ['user-1']
          },
          {
            id: 'msg-3',
            content: 'Super ! Est-ce que je pourrais venir le voir demain ?',
            type: 'text',
            sender_id: 'user-1',
            is_mine: false,
            created_at: new Date(now - 3600000).toISOString(),
            read_by: []
          },
          {
            id: 'msg-4',
            content: 'Bien sûr, je suis disponible à partir de 14h. Je vous envoie l\'adresse en MP.',
            type: 'text',
            sender_id: currentUserId,
            is_mine: true,
            created_at: new Date(now - 1800000).toISOString(),
            read_by: ['user-1']
          },
          {
            id: 'msg-5',
            content: 'Parfait, à demain alors ! 👍',
            type: 'text',
            sender_id: 'user-1',
            is_mine: false,
            created_at: new Date(now - 900000).toISOString(),
            read_by: []
          }
        ];
      } else if (conversation.id === 'demo-2') {
        return [
          {
            id: 'msg-p1',
            content: 'Bonjour, j\'ai bien reçu votre demande de devis pour la rénovation de salle de bain.',
            type: 'text',
            sender_id: 'user-2',
            is_mine: false,
            created_at: new Date(now - 86400000).toISOString(),
            read_by: []
          },
          {
            id: 'msg-p2',
            content: 'Merci pour votre réactivité ! Quand pourriez-vous passer pour voir les travaux ?',
            type: 'text',
            sender_id: currentUserId,
            is_mine: true,
            created_at: new Date(now - 82800000).toISOString(),
            read_by: ['user-2']
          },
          {
            id: 'msg-p3',
            content: 'Je peux passer jeudi matin si ça vous convient. Le devis est prêt, je vous l\'envoie.',
            type: 'text',
            sender_id: 'user-2',
            is_mine: false,
            created_at: new Date(now - 7200000).toISOString(),
            read_by: []
          }
        ];
      } else if (conversation.id === 'demo-3') {
        return [
          {
            id: 'msg-g1',
            content: 'Salut tout le monde ! On fait quoi ce weekend ?',
            type: 'text',
            sender_id: 'user-3',
            is_mine: false,
            sender_name: 'Sophie',
            created_at: new Date(now - 14400000).toISOString(),
            read_by: []
          },
          {
            id: 'msg-g2',
            content: 'On pourrait aller au resto samedi soir ?',
            type: 'text',
            sender_id: currentUserId,
            is_mine: true,
            created_at: new Date(now - 10800000).toISOString(),
            read_by: ['user-3', 'user-4']
          },
          {
            id: 'msg-g3',
            content: 'Bonne idée ! Je connais un super italien pas loin',
            type: 'text',
            sender_id: 'user-4',
            is_mine: false,
            sender_name: 'Lucas',
            created_at: new Date(now - 7200000).toISOString(),
            read_by: []
          },
          {
            id: 'msg-g4',
            content: 'Parfait pour moi 🍕',
            type: 'text',
            sender_id: 'user-3',
            is_mine: false,
            sender_name: 'Sophie',
            created_at: new Date(now - 3600000).toISOString(),
            read_by: []
          },
          {
            id: 'msg-g5',
            content: 'À quelle heure on se retrouve ?',
            type: 'text',
            sender_id: 'user-4',
            is_mine: false,
            sender_name: 'Lucas',
            created_at: new Date(now - 1800000).toISOString(),
            read_by: []
          }
        ];
      }
      return [];
    };
    
    try {
      if (beforeId) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      // Si c'est une conversation de démo, utiliser les messages de démo
      if (conversation.id.startsWith('demo-')) {
        const demoMsgs = getDemoMessages();
        setMessages(demoMsgs);
        setHasMore(false);
        setLoading(false);
        return;
      }
      
      const data = await messagesAPI.getAll(conversation.id, 50, beforeId);
      if (beforeId) {
        setMessages(prev => [...data.messages, ...prev]);
      } else {
        setMessages(data.messages || []);
      }
      setHasMore(data.has_more);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      // Fallback vers messages de démo si erreur
      if (conversation.id.startsWith('demo-')) {
        const demoMsgs = getDemoMessages();
        setMessages(demoMsgs);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [conversation?.id, currentUserId]);

  useEffect(() => {
    loadMessages();
    // Polling de secours uniquement si WebSocket déconnecté
    const interval = setInterval(() => {
      if (!isWebSocketConnected) {
        loadMessages();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [loadMessages, isWebSocketConnected]);

  // Marquer les messages comme lus quand on entre dans la conversation
  useEffect(() => {
    if (messages.length > 0 && onMarkRead) {
      const unreadMessageIds = messages
        .filter(msg => !msg.is_mine && !msg.read_by?.includes(currentUserId))
        .map(msg => msg.id);
      
      if (unreadMessageIds.length > 0) {
        onMarkRead(conversation.id, unreadMessageIds);
      }
    }
  }, [messages, conversation?.id, currentUserId, onMarkRead]);

  useEffect(() => {
    // Scroll to bottom on new messages
    if (messagesEndRef.current && !loadingMore) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loadingMore]);

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && hasMore && !loadingMore && messages.length > 0) {
      loadMessages(messages[0]?.id);
    }
  };

  // ============ SEND MESSAGE ============
  const sendMessage = async (type = 'text', content = '', fileData = null) => {
    if (sending) return;
    try {
      setSending(true);
      const payload = {
        type,
        content,
        ...(fileData && {
          file_url: fileData.file_url,
          file_name: fileData.file_name,
          file_size: fileData.file_size,
          file_mime_type: fileData.file_mime_type,
          voice_duration: fileData.voice_duration
        }),
        ...(replyTo && { reply_to_id: replyTo.id })
      };
      
      await messagesAPI.send(conversation.id, payload);
      setMessageText('');
      setReplyTo(null);
      setAudioBlob(null);
      setSelectedFile(null);
      await loadMessages();
      onConversationUpdate?.();
    } catch (error) {
      console.error('Erreur envoi:', error);
      alert(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (!messageText.trim()) return;
    // Arrêter l'indicateur de frappe
    handleStopTyping();
    sendMessage('text', messageText.trim());
  };

  // Gestion de l'indicateur "en train d'écrire"
  const handleStartTyping = useCallback(() => {
    if (!isTyping && onTyping) {
      setIsTyping(true);
      onTyping(conversation.id, true);
    }
    // Réinitialiser le timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(handleStopTyping, 3000);
  }, [isTyping, onTyping, conversation?.id]);

  const handleStopTyping = useCallback(() => {
    if (isTyping && onTyping) {
      setIsTyping(false);
      onTyping(conversation.id, false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [isTyping, onTyping, conversation?.id]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleTextChange = (e) => {
    setMessageText(e.target.value);
    if (e.target.value.trim()) {
      handleStartTyping();
    } else {
      handleStopTyping();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ============ VOICE RECORDING ============
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Erreur microphone:', error);
      alert('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendVoiceNote = async () => {
    if (!audioBlob) return;
    try {
      setSending(true);
      const file = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
      const uploadResult = await uploadAPI.uploadFile(file);
      await sendMessage('voice', '', {
        ...uploadResult,
        voice_duration: recordingTime
      });
      setAudioBlob(null);
      setRecordingTime(0);
    } catch (error) {
      console.error('Erreur envoi vocal:', error);
      alert('Erreur lors de l\'envoi de la note vocale');
    } finally {
      setSending(false);
    }
  };

  // ============ FILE UPLOAD ============
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('Fichier trop volumineux (max 10MB)');
      return;
    }

    try {
      setUploadingFile(true);
      const uploadResult = await uploadAPI.uploadFile(file);
      
      const isImage = file.type.startsWith('image/');
      await sendMessage(isImage ? 'image' : 'file', file.name, uploadResult);
    } catch (error) {
      console.error('Erreur upload:', error);
      alert(error.message);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ============ DELETE MESSAGE ============
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await messagesAPI.delete(messageId);
      await loadMessages();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
    setContextMenu(null);
  };

  // ============ ARCHIVE ============
  const handleArchive = async () => {
    try {
      await conversationsAPI.toggleArchive(conversation.id);
      onConversationUpdate?.();
      onBack?.();
    } catch (error) {
      console.error('Erreur archivage:', error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getParticipantName = () => {
    if (conversation.type === 'group' && conversation.group_name) {
      return conversation.group_name;
    }
    return conversation.participants?.map(p => p.display_name).join(', ') || 'Conversation';
  };

  // ============ RENDER MESSAGE ============
  const renderMessage = (msg, index) => {
    const isOwn = msg.is_mine;
    const showAvatar = !isOwn && (index === 0 || messages[index - 1]?.sender_id !== msg.sender_id);
    const isDeleted = msg.content === 'Message supprimé';

    return (
      <motion.div
        key={msg.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 px-3`}
      >
        {/* Avatar */}
        {!isOwn && (
          <div className="w-8 mr-2 flex-shrink-0">
            {showAvatar && (
              msg.sender_avatar ? (
                <img src={msg.sender_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )
            )}
          </div>
        )}

        <div 
          className={`max-w-[75%] ${isOwn ? 'order-1' : ''}`}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenu({ x: e.clientX, y: e.clientY, message: msg });
          }}
        >
          {/* Sender name (group) */}
          {!isOwn && showAvatar && conversation.type === 'group' && (
            <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
          )}

          {/* Reply preview */}
          {msg.reply_to_preview && (
            <div className={`text-xs p-2 rounded-t-xl border-l-2 ${isOwn ? 'bg-orange-100 border-orange-500' : 'bg-gray-100 border-gray-400'}`}>
              <Reply className="w-3 h-3 inline mr-1" />
              {msg.reply_to_preview}
            </div>
          )}

          {/* Message bubble */}
          <div
            className={`rounded-2xl px-4 py-2 ${
              isOwn 
                ? 'bg-orange-100 text-gray-900' 
                : 'bg-white border border-gray-200 text-gray-800'
            } ${msg.reply_to_preview ? 'rounded-t-none' : ''} ${isDeleted ? 'opacity-50 italic' : ''}`}
          >
            {/* Voice message */}
            {msg.type === 'voice' && (
              <VoiceMessage 
                url={`${API_URL}${msg.file_url}`} 
                duration={msg.voice_duration}
                isOwn={isOwn}
              />
            )}

            {/* Image */}
            {msg.type === 'image' && msg.file_url && (
              <img 
                src={`${API_URL}${msg.file_url}`}
                alt={msg.file_name}
                className="max-w-full rounded-lg cursor-pointer"
                onClick={() => window.open(`${API_URL}${msg.file_url}`, '_blank')}
              />
            )}

            {/* File */}
            {msg.type === 'file' && (
              <a 
                href={`${API_URL}${msg.file_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-600"
              >
                <File className="w-5 h-5" />
                <span className="underline">{msg.file_name}</span>
                <Download className="w-4 h-4" />
              </a>
            )}

            {/* Text */}
            {(msg.type === 'text' || msg.type === 'system') && (
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
            )}

            {/* Time & Read status */}
            <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-gray-500' : 'text-gray-400'}`}>
              <span className="text-[10px]">{formatTime(msg.created_at)}</span>
              {isOwn && (
                msg.read_by?.length > 1 ? (
                  <CheckCheck className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Check className="w-3.5 h-3.5 text-gray-500" />
                )
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        {/* Avatar */}
        {conversation.type === 'group' ? (
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
        ) : conversation.participants?.[0]?.avatar ? (
          <img src={conversation.participants[0].avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-600" />
          </div>
        )}

        <div className="flex-1">
          <h2 className="font-semibold text-gray-900">{getParticipantName()}</h2>
          {typingUsers && Object.keys(typingUsers).length > 0 ? (
            <p className="text-xs text-green-600 animate-pulse">
              {Object.values(typingUsers).join(', ')} écrit...
            </p>
          ) : conversation.type === 'group' && (
            <p className="text-xs text-gray-500">{conversation.participants?.length + 1} participants</p>
          )}
        </div>

        {/* Boutons d'appel */}
        <div className="flex items-center gap-1">
          <button 
            onClick={() => {
              setCallType('audio');
              setShowCallModal(true);
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Appel vocal"
          >
            <Phone className="w-5 h-5 text-green-600" />
          </button>
          <button 
            onClick={() => {
              setCallType('video');
              setShowCallModal(true);
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Appel vidéo"
          >
            <Video className="w-5 h-5 text-blue-600" />
          </button>
        </div>

        <button 
          onClick={handleArchive}
          className="p-2 hover:bg-gray-100 rounded-full"
          title={conversation.is_archived ? 'Désarchiver' : 'Archiver'}
        >
          <Archive className="w-5 h-5 text-gray-600" />
        </button>
      </header>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto py-4"
        onScroll={handleScroll}
      >
        {loadingMore && (
          <div className="flex justify-center py-2">
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <p>Aucun message</p>
            <p className="text-sm mt-1">Envoyez le premier message !</p>
          </div>
        ) : (
          messages.map((msg, index) => renderMessage(msg, index))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply preview */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-100 border-t border-gray-200 px-4 py-2 flex items-center gap-2"
          >
            <Reply className="w-4 h-4 text-gray-500" />
            <div className="flex-1 truncate text-sm text-gray-600">
              Réponse à: {replyTo.content?.slice(0, 50)}
            </div>
            <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice recording UI */}
      <AnimatePresence>
        {(isRecording || audioBlob) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-orange-50 border-t border-orange-200 px-4 py-3 flex items-center gap-3"
          >
            {isRecording ? (
              <>
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-orange-700 font-medium">{formatDuration(recordingTime)}</span>
                <div className="flex-1 h-1 bg-orange-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 transition-all" 
                    style={{ width: `${(recordingTime / 60) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-orange-600">max 1 min</span>
                <button onClick={stopRecording} className="p-2 bg-orange-500 text-white rounded-full">
                  <StopCircle className="w-5 h-5" />
                </button>
                <button onClick={cancelRecording} className="p-2 text-gray-500 hover:text-red-500">
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            ) : audioBlob && (
              <>
                <Mic className="w-5 h-5 text-orange-500" />
                <span className="text-orange-700">Note vocale ({formatDuration(recordingTime)})</span>
                <div className="flex-1" />
                <button onClick={cancelRecording} className="p-2 text-gray-500 hover:text-red-500">
                  <Trash2 className="w-5 h-5" />
                </button>
                <button 
                  onClick={sendVoiceNote}
                  disabled={sending}
                  className="p-2 bg-orange-500 text-white rounded-full disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input - Design identique aux groupes privés */}
      {!isRecording && !audioBlob && (
        <div className="bg-white border-t border-gray-100 px-4 py-3">
          {/* Inputs fichiers cachés */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div 
            className="flex items-center rounded-full"
            style={{ 
              height: '52px',
              paddingLeft: '16px',
              paddingRight: '8px',
              backgroundColor: '#ececec',
              border: '2px solid #F97316'
            }}
          >
            {/* Bouton + pour menu attachments */}
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className="flex-shrink-0 flex items-center justify-center"
              style={{ color: '#676767' }}
              data-testid="chat-attach-btn"
            >
              <Plus size={24} strokeWidth={2} />
            </button>
            
            {/* Input texte */}
            <input
              data-testid="chat-message-input"
              type="text"
              value={messageText}
              onChange={handleTextChange}
              onKeyPress={handleKeyPress}
              placeholder="Écrire un message..."
              className="flex-1 border-none outline-none shadow-none appearance-none focus:outline-none focus:ring-0 focus:border-none"
              style={{
                marginLeft: '4px',
                marginRight: '8px',
                fontSize: '16px',
                fontWeight: '400',
                color: '#1a1a1a',
                caretColor: '#1a1a1a',
                backgroundColor: 'transparent',
                WebkitAppearance: 'none',
                boxShadow: 'none'
              }}
            />
            
            {/* Bouton micro */}
            <button
              data-testid="chat-btn-voice"
              onClick={startRecording}
              className="flex-shrink-0"
              style={{ marginLeft: '8px', color: '#9a9a9a' }}
            >
              <Mic size={24} strokeWidth={1.5} />
            </button>
            
            {/* Bouton envoyer */}
            <button
              data-testid="chat-btn-send"
              onClick={handleSend}
              disabled={sending || !messageText.trim()}
              className="flex-shrink-0 rounded-full flex items-center justify-center text-white disabled:opacity-50"
              style={{ width: '40px', height: '40px', backgroundColor: '#F97316' }}
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowUp size={20} strokeWidth={2.5} />
              )}
            </button>
          </div>
          
          {/* Menu attachments popup */}
          <AnimatePresence>
            {showAttachMenu && (
              <>
                {/* Overlay */}
                <div 
                  className="fixed inset-0 z-[100]" 
                  style={{ backgroundColor: 'transparent' }}
                  onClick={() => setShowAttachMenu(false)}
                />
                {/* Menu popup */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-4 right-4 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-[101]"
                  style={{ bottom: '80px' }}
                >
                  <div className="p-3">
                    <div className="flex items-center justify-between px-2 pb-2 border-b border-gray-100 mb-2">
                      <p className="text-sm font-semibold text-gray-700">
                        Envoyer un fichier
                      </p>
                      <button
                        onClick={() => setShowAttachMenu(false)}
                        className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                      >
                        <X size={16} className="text-gray-400" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      {/* Photo */}
                      <button
                        onClick={() => {
                          setShowAttachMenu(false);
                          imageInputRef.current?.click();
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                          <ImageIcon size={22} className="text-green-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Photo</span>
                      </button>
                      
                      {/* Vidéo */}
                      <button
                        onClick={() => {
                          setShowAttachMenu(false);
                          videoInputRef.current?.click();
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Video size={22} className="text-purple-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Vidéo</span>
                      </button>
                      
                      {/* Document */}
                      <button
                        onClick={() => {
                          setShowAttachMenu(false);
                          documentInputRef.current?.click();
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText size={22} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-700">Document</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Context menu */}
      <AnimatePresence>
        {contextMenu && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setContextMenu(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[150px]"
              style={{ top: contextMenu.y, left: Math.min(contextMenu.x, window.innerWidth - 160) }}
            >
              <button 
                onClick={() => {
                  setReplyTo(contextMenu.message);
                  setContextMenu(null);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <Reply className="w-4 h-4" /> Répondre
              </button>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(contextMenu.message.content);
                  setContextMenu(null);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <Copy className="w-4 h-4" /> Copier
              </button>
              {contextMenu.message.is_mine && (
                <button 
                  onClick={() => handleDeleteMessage(contextMenu.message.id)}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Supprimer
                </button>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal d'appel vocal/vidéo */}
      <CallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        callType={callType}
        conversation={conversation}
      />
    </div>
  );
};

// ============ VOICE MESSAGE PLAYER ============
const VoiceMessage = ({ url, duration, isOwn }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-3 min-w-[200px]">
      <audio ref={audioRef} src={url} preload="metadata" />
      <button 
        onClick={togglePlay}
        className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-orange-100 hover:bg-orange-200'
        }`}
      >
        {isPlaying ? (
          <Pause className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-orange-600'}`} />
        ) : (
          <Play className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-orange-600'}`} />
        )}
      </button>
      <div className="flex-1">
        <div className={`h-1 rounded-full ${isOwn ? 'bg-white/30' : 'bg-gray-200'}`}>
          <div 
            className={`h-full rounded-full ${isOwn ? 'bg-white' : 'bg-orange-500'}`}
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <span className={`text-xs ${isOwn ? 'text-orange-100' : 'text-gray-500'}`}>
          {formatDuration(currentTime || duration || 0)}
        </span>
      </div>
    </div>
  );
};

export default ChatWindow;
